/**
 * useGlobalSearch Hook
 *
 * Hook for integrating GlobalSearchService with the SearchCommand component.
 * Handles debounced API calls, result mapping, and recent searches persistence.
 */

import {
  ArrowRightOnRectangleIcon,
  BoltIcon,
  CalendarIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  FireIcon,
  HomeIcon,
  PlusIcon,
  ScaleIcon,
  TrophyIcon,
  UserIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { globalSearchService } from '@personal-trainer-app/shared/src/services';
import { MIN_SEARCH_TERM_LENGTH } from '@personal-trainer-app/shared/src/types/domain/search.types';
import {
  getGlobalSearchResultCategory,
  getGlobalSearchResultHref,
  getGlobalSearchResultId,
  getGlobalSearchResultSubtitle,
  getGlobalSearchResultTitle,
} from '@personal-trainer-app/shared/src/utils/global-search-presenter';

import type {
  DefaultItemConfig,
  SvgIconComponent,
  UseGlobalSearchOptions,
  UseGlobalSearchReturn,
} from './useGlobalSearch.types';
import type { SearchResultItem } from '@/components/ui/SearchCommand';
import type { Role } from '@personal-trainer-app/shared/src/types/auth';
import type { GlobalSearchResult } from '@personal-trainer-app/shared/src/types/domain/search.types';

import { useSignOut, useUser } from '@/hooks/auth';
import {
  CHALLENGES_ROUTES,
  CLIENTS_ROUTES,
  MEASUREMENTS_ROUTES,
  PLANS_ROUTES,
  PROTECTED_ROUTES,
  WORKOUTS_ROUTES,
} from '@/utils';
import { IMPERSONATION_COPY } from '@/utils/impersonation';

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Recents are the account's — its plans, its friends, its clients — so they
 * are stored under its username, like the sidebar and questionnaire flags,
 * and the next account on this browser starts with none.
 */
const RECENT_SEARCHES_KEY = 'global-search-recent';
const recentSearchesKey = (username: string): string =>
  `${RECENT_SEARCHES_KEY}:${username}`;
const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_MAX_RECENT = 5;
const SIGNOUT_ACTION_ID = 'action-signout';

// =============================================================================
// ICON COMPONENTS (not elements - we'll render them in the hook)
// =============================================================================

const RESULT_ICON_COMPONENTS: Record<
  GlobalSearchResult['type'],
  SvgIconComponent
> = {
  plan: CalendarIcon,
  workout: BoltIcon,
  exercise: FireIcon,
  challenge: TrophyIcon,
  friend: UserIcon,
  client: UsersIcon,
};

// =============================================================================
// DEFAULT ITEMS CONFIG (without icons - icons added in hook)
// =============================================================================

const CLIENT_DEFAULT_PAGES_CONFIG: DefaultItemConfig[] = [
  {
    id: 'default-dashboard',
    title: 'Dashboard',
    subtitle: 'View your fitness overview',
    category: 'pages',
    href: PROTECTED_ROUTES.DASHBOARD,
    IconComponent: HomeIcon,
  },
  {
    id: 'default-reports',
    title: 'Reports',
    subtitle: 'Track your progress and stats',
    category: 'pages',
    href: PROTECTED_ROUTES.REPORTS,
    IconComponent: ChartBarIcon,
  },
  {
    id: 'default-settings',
    title: 'Settings',
    subtitle: 'Account and app preferences',
    category: 'pages',
    href: PROTECTED_ROUTES.SETTINGS,
    IconComponent: Cog6ToothIcon,
  },
];

const TRAINER_DEFAULT_PAGES_CONFIG: DefaultItemConfig[] = [
  {
    id: 'default-dashboard',
    title: 'Dashboard',
    subtitle: 'View your trainer overview',
    category: 'pages',
    href: PROTECTED_ROUTES.DASHBOARD,
    IconComponent: HomeIcon,
  },
  {
    id: 'default-clients',
    title: 'Clients',
    subtitle: 'Manage your client roster',
    category: 'pages',
    href: CLIENTS_ROUTES.LIST,
    IconComponent: UsersIcon,
  },
  {
    id: 'default-settings',
    title: 'Settings',
    subtitle: 'Account and app preferences',
    category: 'pages',
    href: PROTECTED_ROUTES.SETTINGS,
    IconComponent: Cog6ToothIcon,
  },
];

const CLIENT_DEFAULT_ACTIONS_CONFIG: DefaultItemConfig[] = [
  {
    id: 'action-create-workout',
    title: 'Create New Workout',
    subtitle: 'Create a new workout',
    category: 'actions',
    href: WORKOUTS_ROUTES.NEW,
    IconComponent: BoltIcon,
  },
  {
    id: 'action-create-challenge',
    title: 'Create New Challenge',
    subtitle: 'Create a new fitness challenge',
    category: 'actions',
    href: CHALLENGES_ROUTES.NEW,
    IconComponent: TrophyIcon,
  },
  {
    id: 'action-log-measurement',
    title: 'Log Measurement',
    subtitle: 'Record your body measurements',
    category: 'actions',
    href: MEASUREMENTS_ROUTES.NEW,
    IconComponent: ScaleIcon,
  },
  {
    id: SIGNOUT_ACTION_ID,
    title: 'Sign Out',
    subtitle: 'Log out of your account',
    category: 'actions',
    href: '', // Handled specially via logout()
    IconComponent: ArrowRightOnRectangleIcon,
  },
];

const TRAINER_DEFAULT_ACTIONS_CONFIG: DefaultItemConfig[] = [
  {
    id: 'action-create-plan',
    title: 'Create New Plan',
    subtitle: 'Design a training program',
    category: 'actions',
    href: PLANS_ROUTES.NEW,
    IconComponent: PlusIcon,
  },
  {
    id: 'action-create-workout',
    title: 'Create New Workout',
    subtitle: 'Build a workout template',
    category: 'actions',
    href: WORKOUTS_ROUTES.NEW,
    IconComponent: BoltIcon,
  },
  {
    id: 'action-create-challenge',
    title: 'Create New Challenge',
    subtitle: 'Start a fitness challenge',
    category: 'actions',
    href: CHALLENGES_ROUTES.NEW,
    IconComponent: TrophyIcon,
  },
  {
    id: SIGNOUT_ACTION_ID,
    title: 'Sign Out',
    subtitle: 'Log out of your account',
    category: 'actions',
    href: '', // Handled specially via logout()
    IconComponent: ArrowRightOnRectangleIcon,
  },
];

/**
 * Convert config to SearchResultItem with rendered icon
 */
function configToItem(config: DefaultItemConfig): SearchResultItem {
  const { IconComponent, ...rest } = config;
  return {
    ...rest,
    icon: <IconComponent className="h-4 w-4" />,
  };
}

/**
 * Get default items based on user role. `signOutLabel` retitles Sign Out
 * while it leaves instead (an admin signed in as the member).
 */
function getDefaultItems(
  role: Role | undefined,
  signOutLabel: string | null
): SearchResultItem[] {
  const configs =
    role === 'trainer' || role === 'admin'
      ? [...TRAINER_DEFAULT_PAGES_CONFIG, ...TRAINER_DEFAULT_ACTIONS_CONFIG]
      : [...CLIENT_DEFAULT_PAGES_CONFIG, ...CLIENT_DEFAULT_ACTIONS_CONFIG];

  return configs.map((config) =>
    configToItem(
      config.id === SIGNOUT_ACTION_ID && signOutLabel
        ? {
            ...config,
            title: signOutLabel,
            subtitle: IMPERSONATION_COPY.leaveAccountSubtitle,
          }
        : config
    )
  );
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Load recent searches from localStorage
 * Strips any stale icon data that may have been serialized
 */
function loadRecentSearches(username: string | null): SearchResultItem[] {
  if (!username) return [];
  try {
    const stored = localStorage.getItem(recentSearchesKey(username));
    if (stored) {
      const parsed = JSON.parse(stored) as SearchResultItem[];
      // Strip any stale icon/onSelect data that may have been serialized
      return parsed.map(
        ({ icon: _icon, onSelect: _onSelect, ...rest }) => rest
      );
    }
  } catch {
    // Ignore parse errors
  }
  return [];
}

/**
 * Save recent searches to localStorage
 * Strips non-serializable properties (icon, onSelect) before saving
 */
function saveRecentSearches(
  username: string | null,
  items: SearchResultItem[]
): void {
  if (!username) return;
  try {
    // Strip non-serializable properties before saving
    const serializable = items.map(
      ({ icon: _icon, onSelect: _onSelect, ...rest }) => rest
    );
    localStorage.setItem(
      recentSearchesKey(username),
      JSON.stringify(serializable)
    );
  } catch {
    // Ignore storage errors
  }
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * useGlobalSearch - Hook for global search functionality
 *
 * Integrates with GlobalSearchService to provide search results
 * formatted for the SearchCommand component.
 *
 * @example
 * ```tsx
 * const { results, isLoading, search } = useGlobalSearch();
 *
 * <SearchCommand
 *   isOpen={isOpen}
 *   onClose={close}
 *   results={results}
 *   onSearch={search}
 *   loading={isLoading}
 * />
 * ```
 */
export function useGlobalSearch(
  options: UseGlobalSearchOptions = {}
): UseGlobalSearchReturn {
  const {
    debounceMs = DEFAULT_DEBOUNCE_MS,
    maxRecentItems = DEFAULT_MAX_RECENT,
    enabled = true,
    typeFilter,
  } = options;

  const navigate = useNavigate();
  const { user } = useUser();
  const { signOut: handleSignOut, label: signOutLabel } = useSignOut();
  const username = user?.username ?? null;

  // Get default items based on user role
  const defaultItems = useMemo(
    () => getDefaultItems(user?.role, signOutLabel),
    [user?.role, signOutLabel]
  );

  // State
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);
  const [recentSearches, setRecentSearches] = useState<SearchResultItem[]>(() =>
    loadRecentSearches(username)
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The header stays mounted across an account swap that never flips the
  // auth flag (a successful fetchUser for another user), so the recents
  // follow the username rather than the mount.
  useEffect(() => {
    setRecentSearches(loadRecentSearches(username));
  }, [username]);

  // Refs
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const abortControllerRef = useRef<AbortController>(undefined);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      abortControllerRef.current?.abort();
    };
  }, []);

  /**
   * Map API result to SearchResultItem
   */
  const mapResultToItem = useCallback(
    (result: GlobalSearchResult): SearchResultItem => {
      const href = getGlobalSearchResultHref(result);
      const IconComponent = RESULT_ICON_COMPONENTS[result.type];

      return {
        id: getGlobalSearchResultId(result),
        title: getGlobalSearchResultTitle(result),
        subtitle: getGlobalSearchResultSubtitle(result),
        category: getGlobalSearchResultCategory(result),
        icon: IconComponent ? <IconComponent className="h-4 w-4" /> : undefined,
        href,
        onSelect: () => {
          navigate(href);
        },
      };
    },
    [navigate]
  );

  /**
   * Add item to recent searches
   */
  const addToRecent = useCallback(
    (item: SearchResultItem) => {
      setRecentSearches((prev) => {
        // Remove duplicate if exists
        const filtered = prev.filter((r) => r.id !== item.id);
        // Add to front with recent category
        const recentItem: SearchResultItem = {
          ...item,
          category: 'recent',
        };
        const updated = [recentItem, ...filtered].slice(0, maxRecentItems);
        saveRecentSearches(username, updated);
        return updated;
      });
    },
    [maxRecentItems, username]
  );

  /**
   * Perform the actual search
   */
  const performSearch = useCallback(
    async (searchQuery: string) => {
      if (!enabled) return;

      // Clear results if query is too short
      if (searchQuery.length < MIN_SEARCH_TERM_LENGTH) {
        setSearchResults([]);
        setError(null);
        return;
      }

      // Cancel previous request
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      setError(null);

      try {
        const results = await globalSearchService.search({
          search: searchQuery,
          type: typeFilter,
        });

        const mappedResults = results.map(mapResultToItem);
        setSearchResults(mappedResults);
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError(err.message);
          setSearchResults([]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [enabled, typeFilter, mapResultToItem]
  );

  /**
   * Debounced search handler
   */
  const search = useCallback(
    (searchQuery: string) => {
      setQuery(searchQuery);

      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Clear search results immediately if query is too short
      if (searchQuery.length < MIN_SEARCH_TERM_LENGTH) {
        setSearchResults([]);
        setError(null);
        return;
      }

      // Set new timer for API search
      debounceTimerRef.current = setTimeout(() => {
        performSearch(searchQuery);
      }, debounceMs);
    },
    [debounceMs, performSearch]
  );

  /**
   * Clear all recent searches
   */
  const clearRecent = useCallback(() => {
    setRecentSearches([]);
    if (username) localStorage.removeItem(recentSearchesKey(username));
  }, [username]);

  /**
   * Filter items by query (for local filtering of defaults)
   */
  const filterByQuery = useCallback(
    (items: SearchResultItem[], searchQuery: string): SearchResultItem[] => {
      const lowerQuery = searchQuery.toLowerCase().trim();
      return items.filter(
        (item) =>
          item.title.toLowerCase().includes(lowerQuery) ||
          item.subtitle?.toLowerCase().includes(lowerQuery)
      );
    },
    []
  );

  /**
   * Combine recent, default, and search results
   * - Show recent + defaults when no query
   * - Show filtered defaults + API results when query is present
   */
  const results = useMemo(() => {
    // Helper to wrap items with onSelect handler
    const wrapWithHandler = (item: SearchResultItem): SearchResultItem => ({
      ...item,
      onSelect:
        item.id === SIGNOUT_ACTION_ID
          ? handleSignOut
          : () => {
              addToRecent(item);
              if (item.href) {
                navigate(item.href);
              }
            },
    });

    // Wrap recent items with onSelect
    const recentWithHandlers = recentSearches.map(wrapWithHandler);

    // Wrap default items with onSelect
    const defaultsWithHandlers = defaultItems.map(wrapWithHandler);

    // Wrap search results (from API) with onSelect
    const searchWithHandlers = searchResults.map(wrapWithHandler);

    if (query.length < MIN_SEARCH_TERM_LENGTH) {
      // Show recent + defaults when no active search
      return [...recentWithHandlers, ...defaultsWithHandlers];
    }

    // Filter defaults locally by query
    const filteredDefaults = filterByQuery(defaultsWithHandlers, query);

    // Combine: filtered defaults (pages/actions) + API results
    // API results are already filtered by backend, defaults are filtered locally
    return [...filteredDefaults, ...searchWithHandlers];
  }, [
    query,
    recentSearches,
    searchResults,
    defaultItems,
    addToRecent,
    navigate,
    filterByQuery,
    handleSignOut,
  ]);

  return {
    results,
    isLoading,
    error,
    search,
    clearRecent,
    query,
  };
}

export default useGlobalSearch;
